import React from "react";

// ag-grid builds the chart only after its pivot work, which queues behind every other grid on
// the page, so on a busy dashboard creation alone can outlast the paint failsafe. Both are
// comfortably inside the three minutes browserless waits for the dashboard.
export const CHART_CREATE_FAILSAFE_MS = 60000;
// The same ceiling ag-charts applies to waitForUpdate itself.
export const CHART_PAINT_FAILSAFE_MS = 10000;

const nextFrame = () => new Promise(resolve => requestAnimationFrame(() => resolve()));

// A scheduled report prints the moment every panel has reported loaded, so a panel that
// reports when its data arrives prints before its chart exists. ag-charts creates a chart
// synchronously but paints it over later frames; waitForUpdate settles once it has.
// Reports exactly once, and never waits forever: a report that never hears back fails
// outright, which is worse than one empty chart. A panel whose rows render after its data
// arrives holds off with isReady, and reports through the returned function when it fails
// or has nothing to render.
const useReportWhenPainted = ({isReady = true, expectsChart, chart, onReady}) => {
  const reportedRef = React.useRef(false);
  const onReadyRef = React.useRef(onReady);
  onReadyRef.current = onReady;

  const report = React.useCallback(() => {
    if (reportedRef.current) {
      return;
    }
    reportedRef.current = true;
    if (onReadyRef.current) {
      onReadyRef.current();
    }
  }, []);

  React.useEffect(() => {
    if (!isReady) {
      return undefined;
    }
    if (!expectsChart) {
      report();
      return undefined;
    }
    const failsafe = setTimeout(report, CHART_CREATE_FAILSAFE_MS);
    return () => clearTimeout(failsafe);
  }, [isReady, expectsChart, report]);

  React.useEffect(() => {
    if (!isReady || !expectsChart || !chart) {
      return undefined;
    }
    const failsafe = setTimeout(report, CHART_PAINT_FAILSAFE_MS);
    // A new chart lays out at ag-charts' default 600x300 until a ResizeObserver hands it
    // the panel's size, and waitForUpdate only waits on updates already queued. Observers
    // run after animation frame callbacks, so two frames guarantee that resize is queued.
    nextFrame()
        .then(nextFrame)
        .then(() => chart.waitForUpdate())
        .then(report, report);
    return () => clearTimeout(failsafe);
  }, [isReady, expectsChart, chart, report]);

  return report;
};

export default useReportWhenPainted;
